import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { IDF_SUGGESTIONS } from "@/lib/constants";
import { MapPin, Loader2, X } from "lucide-react";

export const AddressAutocomplete = ({ label, value, onSelect, testId, accent = "#ff6a00" }) => {
  const [query, setQuery] = useState(value?.label || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(!!value?.lat);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    setQuery(value?.label || "");
    setSelected(!!value?.lat);
  }, [value?.label, value?.lat]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const search = (q) => {
    setQuery(q);
    setSelected(false);
    onSelect(null);
    clearTimeout(timer.current);
    if (q.trim().length < 3) {
      setResults([]);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/geo/autocomplete", { params: { q } });
        setResults(data);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 450);
  };

  const pick = (r) => {
    setQuery(r.label);
    setSelected(true);
    setOpen(false);
    onSelect({ label: r.label, lat: r.lat, lon: r.lon });
  };

  const clear = () => {
    setQuery("");
    setSelected(false);
    setResults([]);
    onSelect(null);
  };

  return (
    <div ref={boxRef} className="relative">
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </label>
      <div
        className={`flex items-center gap-2 rounded-xl border bg-[#151b2b] px-3 transition-colors ${
          selected ? "border-emerald-500/50" : "border-[#1F293D] focus-within:border-[#ff6a00]"
        }`}
      >
        <MapPin className="h-4 w-4 shrink-0" style={{ color: selected ? "#10B981" : accent }} />
        <input
          data-testid={testId}
          value={query}
          onChange={(e) => search(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder="Rechercher une adresse en Île-de-France…"
          className="w-full bg-transparent py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none"
        />
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
        {query && !loading && (
          <button type="button" onClick={clear} className="text-slate-500 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {!selected && query.length < 3 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {IDF_SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => search(s)}
              className="rounded-full border border-[#1F293D] bg-[#151b2b] px-2.5 py-1 text-[11px] text-slate-400 transition-colors hover:border-[#ff6a00]/40 hover:text-white"
            >
              {s.split(",")[0]}
            </button>
          ))}
        </div>
      )}

      {open && results.length > 0 && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-[#1F293D] bg-[#151b2b] shadow-2xl">
          {results.map((r, i) => (
            <button
              key={`${r.lat}-${r.lon}-${i}`}
              type="button"
              data-testid={`${testId}-option-${i}`}
              onClick={() => pick(r)}
              className="flex w-full items-start gap-2 border-b border-[#1F293D] px-3 py-2.5 text-left last:border-0 hover:bg-[#1c2334]"
            >
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#ff6a00]" />
              <span className="text-xs text-slate-300">{r.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
